import { all, call, put, takeLatest } from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';
import { propertiesApiService, Property } from '../api';
import { numero } from '@/features/agente/api/propiedad-del-api';
import { mensajeDeFalla } from '../utils/propiedad-edicion';
import {
  getPropertyByIdFailure,
  getPropertyByIdRequest,
  getPropertyByIdSuccess,
  updatePropertyFailure,
  updatePropertyRequest,
  updatePropertySuccess,
  GetPropertyByIdRequest,
  UpdatePropertyRequest,
} from './property-slice';

/**
 * GH#2409 — la respuesta de `GET /properties/:id` es PLANA (`floors`, `yearBuilt`, `condition`, `parkingSpaces`,
 * `location.neighborhood`, `location.lat/lng`, `agent: { id, fullName }`) y este mapeo la llevaba al modelo viejo
 * tirando justo eso, así que el formulario de edición nunca pudo precargarlo. Se conserva todo lo que el API trae.
 * Lo que falta queda `undefined`: antes se inventaba un `0` (habitaciones, baños, área).
 */
export function mapApiPropertyToProperty(apiProperty: any): Property {
  if (!apiProperty) {
    throw new Error('Respuesta de propiedad inválida');
  }

  const location = apiProperty.location || {};
  // `features` es una LISTA de textos en el API; solo si algún día llega el objeto viejo se lee de ahí.
  const features = Array.isArray(apiProperty.features) ? {} : apiProperty.features || {};
  const agente = apiProperty.agent && typeof apiProperty.agent === 'object' ? apiProperty.agent : null;

  const bedrooms = numero(apiProperty.bedrooms) ?? numero(features.bedrooms) ?? undefined;
  const bathrooms = numero(apiProperty.bathrooms) ?? numero(features.bathrooms) ?? undefined;
  const parking = numero(apiProperty.parkingSpaces) ?? numero(features.parking) ?? undefined;
  const floors = numero(apiProperty.floors) ?? numero(features.totalFloors) ?? undefined;
  const yearBuilt = numero(apiProperty.yearBuilt) ?? numero(features.yearBuilt) ?? undefined;
  // `size` es lo que escribe el alta; `coveredAreaM2` lo trae el API cuando se cargó por otra vía.
  const area =
    numero(apiProperty.size) ?? numero(apiProperty.coveredAreaM2) ?? numero(features.area) ?? undefined;
  const lat = numero(location.coordinates?.lat) ?? numero(location.lat) ?? numero(apiProperty.latitude);
  const lng = numero(location.coordinates?.lng) ?? numero(location.lng) ?? numero(apiProperty.longitude);

  return {
    id: apiProperty.id,
    title: apiProperty.title || '',
    description: apiProperty.description || '',
    type: apiProperty.type,
    propertyType: apiProperty.propertyType || apiProperty.type,
    status: apiProperty.status || 'active',
    price: numero(apiProperty.price) ?? 0,
    currency: apiProperty.currency || 'USD',
    bedrooms,
    bathrooms,
    size: area,
    parkingSpaces: parking,
    floors,
    yearBuilt,
    condition: typeof apiProperty.condition === 'string' ? apiProperty.condition : undefined,
    isFeatured: apiProperty.isFeatured === true,
    metadata: apiProperty.metadata && typeof apiProperty.metadata === 'object' ? apiProperty.metadata : undefined,
    location: {
      address: location.address || '',
      city: location.city || '',
      state: location.state || '',
      zipCode: location.zipCode || '',
      neighborhood: typeof location.neighborhood === 'string' ? location.neighborhood : undefined,
      coordinates: lat !== null && lng !== null ? { lat, lng } : undefined,
    },
    features: {
      bedrooms,
      bathrooms,
      area: area ?? 0,
      parking,
      floor: features.floor,
      totalFloors: floors,
      yearBuilt,
      condition: features.condition,
    },
    images: apiProperty.images || [],
    agentId: apiProperty.agentId || agente?.id || '',
    agentName: apiProperty.agentName || agente?.fullName || '',
    listingDate: apiProperty.listingDate
      ? new Date(apiProperty.listingDate)
      : new Date(),
    lastUpdated: apiProperty.lastUpdated
      ? new Date(apiProperty.lastUpdated)
      : new Date(),
    views: apiProperty.views ?? 0,
    inquiries: apiProperty.inquiries ?? 0,
    tags: apiProperty.tags || [],
    amenities: apiProperty.amenities || [],
    createdAt: apiProperty.createdAt ? new Date(apiProperty.createdAt) : new Date(),
    updatedAt: apiProperty.updatedAt ? new Date(apiProperty.updatedAt) : new Date(),
  };
}

function extractPropertyFromResponse(response: any): Property {
  const candidate =
    response?.data?.property ||
    response?.data?.data ||
    response?.data ||
    response;

  return mapApiPropertyToProperty(candidate);
}

function* getPropertyByIdSaga(
  action: PayloadAction<GetPropertyByIdRequest>,
): Generator<any, void, any> {
  try {
    const { id } = action.payload;
    const response: any = yield call(
      propertiesApiService.getPropertyById,
      id,
    );
    const property = extractPropertyFromResponse(response);
    yield put(getPropertyByIdSuccess(property));
  } catch (error: any) {
    const message = mensajeDeFalla(error, 'No se pudo obtener la propiedad seleccionada.');
    yield put(getPropertyByIdFailure(message));
  }
}

function* updatePropertySaga(
  action: PayloadAction<UpdatePropertyRequest>,
): Generator<any, void, any> {
  try {
    const { id, propertyData } = action.payload;
    const response: any = yield call(
      propertiesApiService.updateProperty.bind(propertiesApiService),
      id,
      propertyData,
    );
    const property = extractPropertyFromResponse(response);
    yield put(updatePropertySuccess(property));
  } catch (error: any) {
    const message = mensajeDeFalla(error, 'No se pudo actualizar la propiedad.');
    yield put(updatePropertyFailure(message));
  }
}

export function* watchGetPropertyById() {
  yield takeLatest(getPropertyByIdRequest.type, getPropertyByIdSaga);
}

export function* watchUpdateProperty() {
  yield takeLatest(updatePropertyRequest.type, updatePropertySaga);
}

export function* propertySaga() {
  yield all([watchGetPropertyById(), watchUpdateProperty()]);
}


